import { Entity } from "@backstage/catalog-model";
import { Progress } from "@backstage/core-components";
import { Spacer, useTeams } from "@internal/plugin-utilities";
import { Card, Theme, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React, { useEffect, useState } from "react";
import Carousel from "react-material-ui-carousel";
import { InformationTooltip } from "../../InformationTooltip";
import { SectionHeader } from "../SectionHeader/SectionHeader";
import { TeamCarouselAllGreen } from "../TeamCarouselAllGreen";
import { TeamCarouselItem } from "../TeamCarouselItem";

const useStyles = makeStyles((_theme: Theme) => ({
  loadingCard: {
    margin: "0 100px",
    minHeight: 300,
  },
}));

interface Props {
  teamBreakdownData: any;
  loading: boolean;
}

export const TeamCarousel: React.FC<Props> = ({
  teamBreakdownData,
  loading,
}): JSX.Element => {
  const classes = useStyles();
  const teams = useTeams({ tags: ["ops-review"] });
  const [items, setItems] = useState<any[]>();
  const [allGreen, setAllGreen] = useState<Entity[]>([]);

  useEffect(() => {
    if (teamBreakdownData) {
      // Reset allGreen when processing new data
      setAllGreen([]);

      const greenTeams: Entity[] = [];
      const filtered = Object.entries(teamBreakdownData)
        .sort()
        .map((bd: [string, any]) => {
          const datadogSloViolation = bd[1].datadog?.slos?.violation || false;
          const datadogIncidentsViolation =
            bd[1].datadog?.incidents?.violation || false;
          // const datadogCiRetriesViolation =
          //   bd[1].datadog?.ciRetries?.violation || false;
          const honeybadgerViolation =
            bd[1].honeybadger?.faults?.violation || false;
          const opsgenieViolation =
            bd[1].opsgenie?.incidents?.violation || false;
          // const ownershipViolation =
          //   bd[1].kajabi?.ownership?.violation || false;

          const team = (teams?.value as Entity[])?.find((t: Entity) => {
            // @ts-ignore
            return t.metadata.name === bd[0];
          });

          if (!team) {
            return undefined;
          }

          if (
            // !datadogCiRetriesViolation &&
            !datadogSloViolation &&
            !datadogIncidentsViolation &&
            !honeybadgerViolation &&
            !opsgenieViolation
            // !ownershipViolation
          ) {
            greenTeams.push(team);
            // If there are reviewed incidents still, show them
            if (
              bd[1].opsgenie?.incidents?.data?.length > 0 ||
              bd[1].datadog?.incidents?.data?.length > 0
            ) {
              return (
                <TeamCarouselItem
                  team={team}
                  data={bd[1]}
                  loading={loading}
                  key={team.metadata.name}
                />
              );
            }
            return undefined;
          }

          return (
            <TeamCarouselItem
              team={team}
              data={teamBreakdownData?.[bd[0]]}
              loading={loading}
              key={team.metadata.name}
            />
          );
        })
        .filter((f) => f);

      setAllGreen(greenTeams);
      setItems(filtered);
    }
  }, [teams.value, teamBreakdownData, loading]);

  if (teams.loading && loading) {
    return <Progress />;
  }

  if (teams.error) {
    // console.log(teams.error)
  }

  const tooltip = (
    <>
      <Typography variant="h5">Weekly Team Breakdown</Typography>
      <div>
        This section provides a carousel view of all relevant teams within the
        engineering org. These carousel items will display various information
        that is considered exceptional based on the data provided. The
        expectation being that the team that has the exceptions will be able to
        speak to them at the weekly OpsReview meeting.
      </div>
    </>
  );

  return (
    <>
      <div style={{ textAlign: "center" }}>
        <SectionHeader title="👷‍♂️ Weekly Team Breakdown 👷‍♀️" />
        <InformationTooltip title={tooltip} />
        <Spacer variant="normal" />
      </div>
      <Carousel autoPlay={false} navButtonsAlwaysVisible>
        {items && items.length > 0 ? (
          [
            allGreen.length > 0 ? (
              <TeamCarouselAllGreen teams={allGreen as Entity[]} />
            ) : (
              []
            ),
            ...items,
          ].flat()
        ) : (
          <Card className={classes.loadingCard}>
            <Progress />
          </Card>
        )}
      </Carousel>
    </>
  );
};
